import { FormEventHandler, useMemo, useState } from 'react';

import { Button } from '../../shared/Button';
import { Chip } from '../../shared/Chip';
import ModalV2 from '../../shared/ModalV2';
import { TableColumn } from '../../shared/Table/TableColumn';
import { createSortFn, SortDescriptor } from '../../shared/Table/TableUtils';
import { POST_STATE_COLOR_MAP, POST_STATE_DISPLAY_MAP } from '../Post';
import { PostThumbnail } from '../PostThumbnail';
import { TemplateId } from '@lifeserver/shared';

import { Post, usePortalPosts } from '../../hooks/portal';

import { formatDate } from '../../utils/dates';

import { highlightMatch } from '../portalUiUtils';
import styles from './SelectPostModalSingle.module.scss';

export interface SelectPostModalSingleProps {
    onPostSelected?: (post: Post) => void;
    onClose?: () => void;
    openNewPostModal?: () => void;
    allowedTemplates?: TemplateId[];
}

export const SelectPostModalSingle = ({
    onPostSelected,
    onClose,
    openNewPostModal,
    allowedTemplates,
}: SelectPostModalSingleProps) => {
    const { data: posts } = usePortalPosts();

    const [selectedPost, setSelectedPosts] = useState<Post>();
    const [searchValue, setSearchValue] = useState('');
    const [sortDescriptor, setSortDescriptor] =
        useState<SortDescriptor<Post> | null>({
            column: 'modifiedAt',
            direction: 'desc',
        });

    const filteredPosts = useMemo(() => {
        let filteredPosts = posts;
        const normalizedSearchValue = searchValue.toLowerCase();

        if (allowedTemplates) {
            filteredPosts = filteredPosts?.filter((post) =>
                allowedTemplates.includes(post.templateId as TemplateId),
            );
        }

        if (searchValue) {
            filteredPosts = filteredPosts?.filter(
                (post) =>
                    post.id.toLowerCase().includes(normalizedSearchValue) ||
                    post.tags?.some((tag) =>
                        tag.name.toLowerCase().includes(normalizedSearchValue),
                    ),
            );
        }
        if (sortDescriptor) {
            filteredPosts = filteredPosts?.toSorted(
                createSortFn(sortDescriptor),
            );
        }

        return filteredPosts;
    }, [searchValue, selectedPost, sortDescriptor, posts, allowedTemplates]);

    const togglePost = (post: Post) => {
        setSelectedPosts(post);
    };

    const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        if (selectedPost) {
            onPostSelected?.(selectedPost);
        }
    };

    const onSubmitNewPlanned: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        openNewPostModal?.();
    };

    return (
        <ModalV2 className={styles.modal} onClose={onClose}>
            <header className={styles.header}>
                <h1>Add post to publication</h1>
                <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search"
                    className={styles.searchBar}
                    role="search"
                />

                <Button color="secondary" onClick={onClose}>
                    X
                </Button>
            </header>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <TableColumn style={{ width: 0 }}>
                            {/* Checkbox */}
                        </TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="state"
                        >
                            State
                        </TableColumn>
                        <TableColumn style={{ width: 0 }}>
                            {/* Thumbnail */}
                        </TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="id"
                        >
                            Name
                        </TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="modifiedAt"
                        >
                            Modified
                        </TableColumn>
                        <TableColumn>Tags</TableColumn>
                    </tr>
                </thead>
                <tbody>
                    {!filteredPosts
                        ? null
                        : filteredPosts.map((post) => (
                              <tr
                                  key={post.id}
                                  onClick={() => {
                                      togglePost(post);
                                  }}
                              >
                                  <td className={styles.checkbox}>
                                      <input
                                          type="checkbox"
                                          checked={selectedPost?.id === post.id}
                                          onChange={() => {
                                              togglePost(post);
                                          }}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                          }}
                                      />
                                  </td>
                                  <td>
                                      <Chip
                                          customColor={
                                              POST_STATE_COLOR_MAP[post.state]
                                          }
                                          customTextColor={
                                              post.state === 'todo'
                                                  ? '#000000'
                                                  : '#FFFFFF'
                                          }
                                      >
                                          {POST_STATE_DISPLAY_MAP[post.state]}
                                      </Chip>
                                  </td>
                                  <td>
                                      <PostThumbnail
                                          className={styles.thumbnail}
                                          postData={post.payload}
                                          templateId={post.templateId}
                                      />
                                  </td>
                                  <td>
                                      {highlightMatch(
                                          post.id,
                                          searchValue,
                                          'highlight',
                                      )}
                                  </td>
                                  <td>
                                      <p className={styles.timestamp}>
                                          {formatDate(post.modifiedAt)}
                                      </p>
                                      <p className={styles.author}>
                                          {post.modifiedBy ?? 'Unknown'}
                                      </p>
                                  </td>
                                  <td className={styles.tagsColumn}>
                                      {post.tags && post.tags.length > 0 && (
                                          <div className={styles.tagsContainer}>
                                              {post.tags
                                                  .slice(0, 3)
                                                  .map((tag, index) => (
                                                      <Chip
                                                          key={index}
                                                          color="secondary"
                                                      >
                                                          {tag.name}
                                                      </Chip>
                                                  ))}
                                              {post.tags.length > 3 && (
                                                  <Chip color="secondary">
                                                      +{post.tags.length - 3}
                                                  </Chip>
                                              )}
                                          </div>
                                      )}
                                  </td>
                              </tr>
                          ))}
                </tbody>
            </table>
            <footer className={styles.footer}>
                <div className={styles.buttonGroup}>
                    <form className={styles.form} onSubmit={onSubmitNewPlanned}>
                        <Button type="submit" color="primary">
                            Add New Post
                        </Button>
                    </form>
                    <form className={styles.form} onSubmit={onSubmit}>
                        <Button disabled={!selectedPost} type="submit">
                            Add Selected Post
                        </Button>
                    </form>
                </div>
            </footer>
        </ModalV2>
    );
};
